// src/pages/Admin/AdminLogin.jsx
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';
import Layout from '../../components/common/Layout/Layout';
import logoImage from '../../assets/images/winnestlogo.png';

const AdminLogin = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { updateAuthStatus } = useAuth();

    // Get the intended destination (if user was redirected here)
    const from = location.state?.from?.pathname || '/adminportal';

    const [formData, setFormData] = useState({
        username: '',
        password: '',
        rememberMe: false
    });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = {};

        if (!formData.username.trim()) {
            newErrors.username = 'Username is required';
        }

        if (!formData.password.trim()) {
            newErrors.password = 'Password is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsLoading(true);

        try {
            // Check if API base URL is configured
            const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
            
            if (apiBaseUrl) {
                // API call to Spring Boot backend
                const response = await fetch(`${apiBaseUrl}/api/auth/login`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        username: formData.username,
                        password: formData.password,
                        rememberMe: formData.rememberMe
                    })
                });

                const data = await response.json();

                if (data.success) {
                    console.log('Login successful:', data.message);
                    
                    // IMPORTANT: Save the JWT token for API authentication
                    if (data.token) {
                        localStorage.setItem('authToken', data.token);
                        console.log('JWT token saved for API authentication');
                    }
                    
                    // Update auth context with user data
                    updateAuthStatus({
                        id: data.user?.id || 1,
                        username: formData.username,
                        email: data.user?.email || formData.username,
                        name: data.user?.name || formData.username,
                        role: 'admin'
                    }, formData.rememberMe);
                    
                    // Redirect to the intended page or admin portal
                    navigate(from, { replace: true });
                } else {
                    setErrors({
                        username: data.message || 'Invalid credentials',
                        password: data.message || 'Invalid credentials'
                    });
                }
            } else {
                // Fallback for development/testing without backend
                console.log('No API configured, using mock authentication');
                
                // Simple mock authentication for testing
                if (formData.username === 'admin' && formData.password === 'admin123') {
                    updateAuthStatus({
                        id: 1,
                        username: formData.username,
                        email: 'admin@winnest.in',
                        name: 'Admin User',
                        role: 'admin'
                    }, formData.rememberMe);
                    
                    navigate(from, { replace: true });
                } else {
                    setErrors({
                        username: 'Invalid username or password',
                        password: 'Invalid username or password'
                    });
                }
            }
        } catch (error) {
            console.error('Login error:', error);
            setErrors({
                username: 'Network error. Please try again.',
                password: 'Network error. Please try again.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const styles = {
        pageContainer: {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff'
        },
        // Main container
        container: {
            flex: 1,
            background: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem 1rem',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            minHeight: '70vh'
        },

        // Main login card
        loginCard: {
            width: '100%',
            maxWidth: '28rem',
            background: '#ffffff',
            borderRadius: '12px',
            padding: '2rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb'
        },

        // Logo section
        logoSection: {
            textAlign: 'center',
            paddingBottom: '1.5rem',
            marginBottom: '1.5rem',
            borderBottom: '1px solid #f3f4f6'
        },

        logoContainer: {
            position: 'relative',
            display: 'inline-block',
            marginBottom: '1rem'
        },

        logoImage: {
            width: '4rem',
            height: '4rem',
            borderRadius: '12px',
            objectFit: 'contain',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            backgroundColor: '#ffffff',
            padding: '0.5rem'
        },

        // Fallback logo when image is not available
        logoFallback: {
            width: '4rem',
            height: '4rem',
            background: '#22c55e',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            color: 'white',
            fontSize: '1.5rem',
            fontWeight: 'bold'
        },

        verificationBadge: {
            position: 'absolute',
            top: '-0.25rem',
            right: '-0.25rem',
            width: '1.5rem',
            height: '1.5rem',
            background: '#22c55e',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            color: 'white',
            fontSize: '0.75rem'
        },

        companyName: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#000000', // Changed to black
            marginBottom: '0.5rem',
            lineHeight: '1.2'
        },

        tagline: {
            color: '#6b7280',
            fontSize: '0.875rem',
            marginBottom: '0.25rem',
            lineHeight: '1.3'
        },

        distributor: {
            color: '#9ca3af',
            fontSize: '0.75rem',
            fontWeight: '500'
        },

        // Welcome section
        welcomeSection: {
            textAlign: 'center',
            marginBottom: '1.5rem'
        },

        welcomeTitle: {
            fontSize: '1.875rem',
            fontWeight: 'bold',
            color: '#1a1a1a',
            marginBottom: '0.5rem',
            lineHeight: '1.2'
        },

        welcomeSubtitle: {
            fontSize: '1rem',
            color: '#6b7280'
        },

        // Form styles
        form: {
            marginBottom: '1rem'
        },

        formGroup: {
            marginBottom: '1.5rem'
        },

        label: {
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '0.5rem'
        },

        inputContainer: {
            position: 'relative'
        },

        input: {
            width: '100%',
            padding: '0.75rem 1rem',
            background: '#f9fafb',
            border: '2px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '1rem',
            transition: 'all 0.2s ease',
            outline: 'none',
            boxSizing: 'border-box'
        },

        inputFocus: {
            borderColor: '#22c55e',
            background: '#ffffff',
            boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.1)'
        },

        inputError: {
            borderColor: '#ef4444',
            background: '#fef2f2'
        },

        inputIcon: {
            position: 'absolute',
            right: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9ca3af',
            fontSize: '1.25rem'
        },

        passwordToggle: {
            position: 'absolute',
            right: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            padding: '0.25rem',
            transition: 'color 0.2s ease'
        },

        errorMessage: {
            display: 'flex',
            alignItems: 'center',
            color: '#ef4444',
            fontSize: '0.875rem',
            marginTop: '0.5rem'
        },

        errorIcon: {
            width: '1rem',
            height: '1rem',
            marginRight: '0.5rem'
        },

        // Checkbox and controls
        controlsRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1.5rem'
        },

        checkboxContainer: {
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer'
        },

        checkboxWrapper: {
            position: 'relative',
            marginRight: '0.75rem'
        },

        checkbox: {
            position: 'absolute',
            opacity: 0,
            cursor: 'pointer'
        },

        checkboxCustom: {
            width: '1.25rem',
            height: '1.25rem',
            border: '2px solid #d1d5db',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
        },

        checkboxChecked: {
            background: '#22c55e',
            borderColor: '#22c55e'
        },

        checkboxLabel: {
            fontSize: '0.875rem',
            color: '#4b5563'
        },

        forgotPassword: {
            fontSize: '0.875rem',
            color: '#22c55e',
            textDecoration: 'none',
            fontWeight: '500',
            transition: 'color 0.2s ease'
        },

        // Buttons
        loginButton: {
            width: '100%',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            fontWeight: '600',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontSize: '1rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#22c55e'
        },

        loginButtonHover: {
            background: '#16a34a',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 6px -1px rgba(34, 197, 94, 0.4)'
        },

        loginButtonLoading: {
            background: '#9ca3af',
            cursor: 'not-allowed'
        },

        // Spinner
        spinner: {
            width: '1.25rem',
            height: '1.25rem',
            border: '2px solid #ffffff',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginRight: '0.75rem'
        },

        // Footer
        footer: {
            textAlign: 'center',
            paddingTop: '1rem',
            borderTop: '1px solid #f3f4f6'
        },

        footerItem: {
            marginBottom: '0.75rem'
        },

        footerText: {
            fontSize: '0.875rem',
            color: '#6b7280'
        },

        footerLink: {
            fontSize: '0.875rem',
            color: '#22c55e',
            textDecoration: 'none',
            fontWeight: '500',
            transition: 'color 0.2s ease'
        },

        securityBadge: {
            paddingTop: '1rem',
            borderTop: '1px solid #f3f4f6',
            marginTop: '0.75rem'
        },

        securityContent: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            color: '#6b7280'
        },

        securityIcon: {
            width: '1rem',
            height: '1rem',
            color: '#22c55e',
            marginRight: '0.5rem'
        }
    };

    return (
        <>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .login-input:focus {
                    border-color: #22c55e !important;
                    background: #ffffff !important;
                    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1) !important;
                }

                .login-input.error {
                    border-color: #ef4444 !important;
                    background: #fef2f2 !important;
                }

                .login-button:hover:not(:disabled) {
                    background: #16a34a !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 4px 6px -1px rgba(34, 197, 94, 0.4) !important;
                }

                .forgot-link:hover {
                    color: #16a34a !important;
                    text-decoration: underline !important;
                }

                .footer-link:hover {
                    color: #16a34a !important;
                    text-decoration: underline !important;
                }

                .password-toggle:hover {
                    color: #4b5563 !important;
                }

                @media (max-width: 768px) {
                    .login-card {
                        margin: 1rem;
                        padding: 1.5rem;
                    }
                    
                    .welcome-title {
                        font-size: 1.5rem !important;
                    }
                }
                
                @media (max-width: 640px) {
                    .container {
                        padding: 1rem 0.5rem;
                    }
                    
                    .login-card {
                        padding: 1rem;
                    }
                    
                    .welcome-title {
                        font-size: 1.25rem !important;
                    }
                }
            `}</style>

            <div style={styles.pageContainer}>
                {/* Header */}
                <Header />

                {/* Main Login Section */}
                <div style={styles.container}>
                    <div style={styles.loginCard} className="login-card">
                        {/* Logo Section */}
                        <div style={styles.logoSection}>
                            <div style={styles.logoContainer}>
                                {/* Winnest Logo Image - same as Header */}
                                <img 
                                    src={logoImage} 
                                    alt="Winnest Logo"
                                    style={styles.logoImage}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                                <div style={{
                                    ...styles.logoFallback,
                                    display: 'none' // Will show if image fails to load
                                }}>
                                    W
                                </div>
                                {/* Verification badge removed */}
                            </div>
                            <h2 style={styles.companyName}>Winnest</h2>
                            <p style={styles.tagline}>Creating Happy People, Happy Nation</p>
                            <p style={styles.distributor}>AMFI-Registered Mutual Fund Distributor</p>
                        </div>

                        {/* Welcome Section */}
                        <div style={styles.welcomeSection}>
                            <h1 style={styles.welcomeTitle} className="welcome-title">Welcome Back!</h1>
                            <p style={styles.welcomeSubtitle}>Sign in to access your admin portal</p>
                        </div>

                        {/* Login Form */}
                        <form onSubmit={handleSubmit} style={styles.form}>
                            {/* Username Field */}
                            <div style={styles.formGroup}>
                                <label htmlFor="username" style={styles.label}>
                                    Username
                                </label>
                                <div style={styles.inputContainer}>
                                    <input
                                        type="text"
                                        id="username"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                        className={`login-input ${errors.username ? 'error' : ''}`}
                                        style={{
                                            ...styles.input,
                                            ...(errors.username ? styles.inputError : {})
                                        }}
                                        placeholder="Enter your username"
                                    />
                                    <div style={styles.inputIcon}>👤</div>
                                </div>
                                {errors.username && (
                                    <div style={styles.errorMessage}>
                                        <span style={styles.errorIcon}>⚠️</span>
                                        {errors.username}
                                    </div>
                                )}
                            </div>

                            {/* Password Field */}
                            <div style={styles.formGroup}>
                                <label htmlFor="password" style={styles.label}>
                                    Password
                                </label>
                                <div style={styles.inputContainer}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                        className={`login-input ${errors.password ? 'error' : ''}`}
                                        style={{
                                            ...styles.input,
                                            paddingRight: '3rem',
                                            ...(errors.password ? styles.inputError : {})
                                        }}
                                        placeholder="Enter your password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        disabled={isLoading}
                                        className="password-toggle"
                                        style={styles.passwordToggle}
                                    >
                                        {showPassword ? '🙈' : '👁️'}
                                    </button>
                                </div>
                                {errors.password && (
                                    <div style={styles.errorMessage}>
                                        <span style={styles.errorIcon}>⚠️</span>
                                        {errors.password}
                                    </div>
                                )}
                            </div>

                            {/* Controls Row */}
                            <div style={styles.controlsRow}>
                                <label style={styles.checkboxContainer}>
                                    <div style={styles.checkboxWrapper}>
                                        <input
                                            type="checkbox"
                                            name="rememberMe"
                                            checked={formData.rememberMe}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                            style={styles.checkbox}
                                        />
                                        <div style={{
                                            ...styles.checkboxCustom,
                                            ...(formData.rememberMe ? styles.checkboxChecked : {})
                                        }}>
                                            {formData.rememberMe && <span style={{ color: 'white', fontSize: '0.75rem' }}>✓</span>}
                                        </div>
                                    </div>
                                    <span style={styles.checkboxLabel}>Remember me</span>
                                </label>
                                <Link 
                                    to="/forgot-password" 
                                    className="forgot-link"
                                    style={styles.forgotPassword}
                                >
                                    Forgot password?
                                </Link>
                            </div>

                            {/* Login Button */}
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="login-button"
                                style={{
                                    ...styles.loginButton,
                                    ...(isLoading ? styles.loginButtonLoading : {})
                                }}
                            >
                                {isLoading ? (
                                    <>
                                        <div style={styles.spinner}></div>
                                        Signing you in...
                                    </>
                                ) : (
                                    'Sign In to Portal'
                                )}
                            </button>
                        </form>

                        {/* Footer */}
                        <div style={styles.footer}>
                            {/* Security Badge */}
                            <div style={styles.securityBadge}>
                                <div style={styles.securityContent}>
                                    <span style={styles.securityIcon}>🔒</span>
                                    <span>Secured by 256-bit SSL encryption</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Layout */}
                <Layout />

                {/* Footer */}
                <Footer />
            </div>
        </>
    );
};

export default AdminLogin;